import { useState, useContext } from "react";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { CourseStructureCtx, UserCtx } from "../../interfaces";
import { Box, Breadcrumbs, Link, Stack, Typography } from "@mui/material";
import { ModuleOverviewHeader } from "../../components/v2/module/ModuleOverviewHeader";
import { ModuleTargetList } from "../../components/v2/module/ModuleTargetList";
import { ModuleSummaryCard } from "../../components/v2/module/ModuleSummaryCard";
import { SkillVideoModal } from "../../components/v2/SkillVideoModal";
import { PageContainer } from "../../components/v2/PageContainer";
import { useHelpAndResources } from "../../hooks/useHelpAndResources";
import { HelpAndResourcesOptions } from "../../enums";
import {
  Target,
  TargetSkill as TargetSkillTree,
} from "../../components/utils/CourseTree";
import { UserContext } from "../../context/UserContext";
import { useHistory } from "react-router-dom";
import { generateGtmEvent } from "../../Helpers";
import { Helmet } from "react-helmet-async";
import { ModuleTargetGroupList } from "../../components/v2/module/ModuleTargetGroupList";
import { ModuleSectionCarousel } from "../../components/v2/explore/ModuleSectionCarousel";

export const ModulePage = () => {
  const history = useHistory();
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();

  const userCtx = useContext(UserContext) as UserCtx;
  let currentModuleId = courseStructureCtx.currentModuleId;
  let currentModule = courseStructureCtx.courseTreeStructure?.getModule(currentModuleId);
  let moduleTargetGroups = currentModule?.target_groups ?? [];
  let moduleTargets =
    courseStructureCtx.courseTreeStructure?.getAllTargetsInModule(currentModuleId) ?? [];
  const currentModuleName = currentModule?.getFormattedModuleName();
  const [skillVideoOpen, setSkillVideoModalOpen] = useState(false);
  const [skillVideoSkills, setSkillsVideoSkills] = useState<TargetSkillTree[]>();
  const handleOpenSkillVideoModal = (skills: TargetSkillTree[] | undefined) => {
    if (!skills) return;
    setSkillVideoModalOpen(true);
    setSkillsVideoSkills(skills);
  };
  const { element: HelpAndResourcesButton } = useHelpAndResources({
    hideHelpAndResourcesOption: [HelpAndResourcesOptions.GUIDED_TOUR],
    toolTipProps: {
      steps: [
        {
          header: "Getting started",
          body: "You can exit the tour and restart it anytime from this Help and Resources button. Use the button to see what resources are available to you.",
          position: "rightbottom",
          mountPosition: "left",
          targetId: "help-and-resources",
        },
        {
          header: "Let's start learning!",
          body: "If you need any help or support, send an email to digitsupport@asu.edu and we will get back to you in 24 hours.",
          position: "center",
          mountPosition: "centerscreen",
          targetId: "main",
        },
      ],
      parentId: "main",
    },
  });
  const resumeAtQuestion =
    userCtx.getProblemToResumeOnModule(currentModule?.id ?? "", false) ?? undefined;
  const resumeAtTarget = resumeAtQuestion?.parent as Target | undefined;

  const currentCourse = courseStructureCtx.courseTreeStructure?.getCurrentCourse();
  const useDigitStudyTool = currentCourse?.metadata.use_digit_study_tool ?? false;

  return (
    <>
      <Helmet>
        <title>{currentModuleName!} | Digit</title>
      </Helmet>
      <PageContainer>
        <Stack display={"flex"} flexDirection={"row"} sx={{ gap: 4 }}>
          <Stack flexGrow={1}>
            {useDigitStudyTool && (
              <Breadcrumbs
                separator="›"
                aria-label="breadcrumb"
                sx={{ marginBottom: "16px" }}>
                <Link
                  tabIndex={0}
                  underline="none"
                  color="inherit"
                  onClick={() => history.push("/learning-overview")}>
                  Home
                </Link>
                <Typography sx={{ color: "#007CBF" }}>
                  {courseMetadata?.module_overview_page?.disable_module_number ? (
                    <>{currentModuleName}</>
                  ) : (
                    <>Module {currentModule?.order}</>
                  )}
                </Typography>
              </Breadcrumbs>
            )}
            <ModuleOverviewHeader
              disableBack={
                (courseMetadata?.module_overview_page?.disable_back_navigation ||
                  courseMetadata?.deeplink_asset_type === "target") &&
                courseStructureCtx.backBehavior.backTo !== "deeplinking"
              }
              backButtonLabel={"Back Home"}
              onBackClick={() => {
                generateGtmEvent({
                  event: "link",
                  action: "click",
                  name: "onclick",
                  type: "internal link",
                  region: "main content",
                  section: `${currentModuleName!}`,
                  text: "back home",
                });
                if (courseStructureCtx.backBehavior.backTo === "deeplinking") {
                  history.push({
                    pathname: courseStructureCtx.backBehavior.route,
                    search: courseStructureCtx.backBehavior.search,
                  });
                } else {
                  history.push({
                    pathname: "/learning-overview",
                  });
                }
              }}>
              <Box aria-live="assertive" aria-atomic="true">
                <Typography variant="h1" sx={{ fontWeight: "bold", color: "#191919" }}>
                  {currentModuleName!}
                </Typography>
              </Box>
              <Typography
                variant="h3"
                component={"span"}
                sx={{ fontWeight: "bold", color: "#191919" }}>
                {courseMetadata?.language_overrides?.target
                  ? courseMetadata?.language_overrides?.target.plural
                  : "Learning Targets"}
              </Typography>
            </ModuleOverviewHeader>
            <Box sx={{ marginTop: "32px" }}>
              {moduleTargetGroups.length > 0 ? (
                <ModuleTargetGroupList
                  targetGroups={moduleTargetGroups}
                  targets={moduleTargets}
                  openSkillVideoModal={handleOpenSkillVideoModal}
                  languageOverrides={courseMetadata?.language_overrides}
                />
              ) : (
                <ModuleTargetList
                  targets={moduleTargets}
                  openSkillVideoModal={handleOpenSkillVideoModal}
                  languageOverrides={courseMetadata?.language_overrides}
                />
              )}
            </Box>
          </Stack>

          <Stack sx={{ width: "338px" }}>
            <ModuleSummaryCard
              moduleId={currentModuleId}
              moduleTitle={currentModuleName ?? ""}
              moduleText={currentModule?.landing_page ?? ""}
              targetText={currentModule?.landing_page_target}
              isDemo={userCtx.studentCurrentClass?.is_demo ?? false}
              progress={currentModule?.getProgress() ?? 0}
              resumeQuestionId={
                !resumeAtTarget?.is_asset_locked ? resumeAtQuestion?.id : undefined
              }
            />
            {useDigitStudyTool && (
              <ModuleSectionCarousel
                moduleList={currentCourse?.children ?? []}
                currentModuleId={currentModuleId}
              />
            )}
          </Stack>
        </Stack>
        {/* <HelpAndResourcesButton
      guidedTourProps={guidedTourProps}
      guidedTourOpen={guidedTourOpen}
      closeGuidedTour={handleCloseGuidedTour}
      openGuidedTour={handleOpenGuidedTour}
    /> */}
        <SkillVideoModal
          open={skillVideoOpen}
          skills={skillVideoSkills}
          onClose={() => setSkillVideoModalOpen(false)}
          showControls={true}
          gtmSection={currentModuleName!}
        />
        {HelpAndResourcesButton}
      </PageContainer>
    </>
  );
};
